import Phaser from 'phaser';
import {
  DROP_INITIAL_SPEED,
  FIELD_HEIGHT,
  FIELD_WIDTH,
} from '../../config/gameplay';
import type { CharacterId } from '../../game/content/characters';
import type { SessionEvent, SessionState } from '../../game/simulation/types';
import type { SceneBridge } from '../adapters/SceneBridge';
import type { BodyFactory } from '../physics/BodyFactory';



export type DropPreviewSnapshot = Readonly<{
  characterId: CharacterId;
  x: number;
  y: number;
  originX: number;
  originY: number;
  displayWidth: number;
  displayHeight: number;
  visible: boolean;
  canControl: boolean;
}>;

export type DropFlowControllerOptions = Readonly<{
  scene: Phaser.Scene;
  bodies: BodyFactory;
  bridge: SceneBridge;
  getState: () => SessionState;
  getStep: () => number;
  getGameplayTimeMs: () => number;
  onPreviewMoved: (x: number) => void;
}>;

export class DropFlowController {
  private readonly scene: Phaser.Scene;
  private readonly bodies: BodyFactory;
  private readonly bridge: SceneBridge;
  private readonly getState: () => SessionState;
  private readonly getStep: () => number;
  private readonly getGameplayTimeMs: () => number;
  private readonly onPreviewMoved: (x: number) => void;
  private readonly preview: Phaser.GameObjects.Image;
  private readonly dropGuide: Phaser.GameObjects.Graphics;
  private previewX = FIELD_WIDTH / 2;
  private dropWindowEntityId: string | null = null;
  private hardDropEntityId: string | null = null;
  private continuousDropEnabled = false;

  public constructor(options: DropFlowControllerOptions) {
    this.scene = options.scene;
    this.bodies = options.bodies;
    this.bridge = options.bridge;
    this.getState = options.getState;
    this.getStep = options.getStep;
    this.getGameplayTimeMs = options.getGameplayTimeMs;
    this.onPreviewMoved = options.onPreviewMoved;
    const current = this.bodies.characterDefinition(this.getState().currentCharacterId);
    this.preview = this.scene.add.image(this.previewX, this.spawnY(), current.textureKey);
    this.preview.setDepth(150).setAlpha(0.72).setVisible(false);
    this.dropGuide = this.scene.add.graphics().setDepth(149).setVisible(false);
  }

  private spawnY(): number {
    return this.bodies.dropY(this.getState().currentCharacterId);
  }

  public handleResult(events: readonly SessionEvent[]): void {
    if (events.some((event) => event.type === 'phase-changed')) {
      this.dropWindowEntityId = null;
      this.hardDropEntityId = null;
      this.previewX = FIELD_WIDTH / 2;
    }
    const createdDrop = events.find(
      (event): event is Extract<SessionEvent, { type: 'drop-created' }> =>
        event.type === 'drop-created' && event.origin === 'normal',
    );
    if (createdDrop) this.dropWindowEntityId = createdDrop.entityId;
    const finished = events.find(
      (event): event is Extract<SessionEvent, { type: 'drop-finished' }> => event.type === 'drop-finished',
    );
    if (finished && finished.entityId === this.dropWindowEntityId) {
      this.dropWindowEntityId = null;
      this.hardDropEntityId = null;
      this.startControlledDrop();
    }
    this.maintainControlledFall();
    this.syncPreview();
  }

  public setContinuousDrop(enabled: boolean): void {
    this.continuousDropEnabled = enabled;
  }

  public reset(continuousDropEnabled: boolean): void {
    this.dropWindowEntityId = null;
    this.hardDropEntityId = null;
    this.continuousDropEnabled = continuousDropEnabled;
  }

  public movePreview(requestedX: number): void {
    const state = this.getState();
    const activeEntity = state.entities.find((entity) => entity.entityId === state.activeDropEntityId);
    const character = this.bodies.characterDefinition(activeEntity?.characterId ?? state.currentCharacterId);
    const margin = Math.max(26, character.baseSize / 2 + 7);
    this.previewX = Phaser.Math.Clamp(requestedX, margin, FIELD_WIDTH - margin);
    const activeBody = state.activeDropEntityId ? this.bodies.get(state.activeDropEntityId) : undefined;
    if (activeBody && !activeEntity?.placed && this.hardDropEntityId !== activeBody.entityId) {
      this.scene.matter.body.setPosition(activeBody.body, { x: this.previewX, y: activeBody.body.position.y });
      this.scene.matter.body.setVelocity(activeBody.body, { x: 0, y: activeBody.body.velocity.y });
      this.scene.matter.body.setAngularVelocity(activeBody.body, 0);
    } else {
      this.preview.setX(this.previewX);
    }
    this.onPreviewMoved(this.previewX);
    this.updateGuide();
  }

  public drop(): void {
    if (!this.canControl()) return;
    const activeDropEntityId = this.getState().activeDropEntityId;
    if (activeDropEntityId) {
      const activeBody = this.bodies.get(activeDropEntityId);
      if (!activeBody) return;
      this.hardDropEntityId = activeDropEntityId;
      this.bridge.confirmDrop(activeDropEntityId, this.getGameplayTimeMs());
      activeBody.image.setIgnoreGravity(false);
      activeBody.body.collisionFilter.mask = 0xffff_ffff;
      activeBody.image.setAwake();
      this.scene.matter.body.setVelocity(activeBody.body, { x: 0, y: DROP_INITIAL_SPEED });
      this.dropGuide.setVisible(false);
      return;
    }
    this.preview.setVisible(false);
    this.dropGuide.setVisible(false);
    this.bridge.beginDrop(this.previewX, this.spawnY(), this.getStep(), this.getGameplayTimeMs());
    this.hardDropEntityId = this.getState().activeDropEntityId;
    if (this.hardDropEntityId) {
      this.bridge.confirmDrop(this.hardDropEntityId, this.getGameplayTimeMs());
      const body = this.bodies.get(this.hardDropEntityId);
      if (body) {
        body.image.setIgnoreGravity(false);
        body.body.collisionFilter.mask = 0xffff_ffff;
        this.scene.matter.body.setVelocity(body.body, { x: 0, y: DROP_INITIAL_SPEED });
      }
    }
  }

  public dropAt(x: number): void {
    this.movePreview(x);
    this.drop();
  }

  public canControl(): boolean {
    const state = this.getState();
    if (state.status !== 'playing') return false;
    if (state.activeDropEntityId === null) return true;
    return state.dropView.canControl && state.activeDropEntityId !== this.hardDropEntityId;
  }

  public syncPreview(): void {
    const state = this.getState();
    const character = this.bodies.characterDefinition(state.currentCharacterId);
    this.preview.setTexture(character.textureKey);
    this.preview.setY(this.spawnY());
    this.preview.setDisplaySize(character.baseSize, character.baseSize);
    this.preview.setOrigin(0.5 + character.centerOfMass.x, 0.5 + character.centerOfMass.y);
    if (state.activeDropEntityId === null) this.movePreview(this.previewX);
    const previewVisible = this.canControl() && state.activeDropEntityId === null;
    this.preview.setVisible(previewVisible);
    this.dropGuide.setVisible(this.canControl());
    if (this.canControl()) this.updateGuide();
  }

  public updateGuide(): void {
    const state = this.getState();
    const activeEntity = state.entities.find((entity) => entity.entityId === state.activeDropEntityId);
    const activeBody = activeEntity ? this.bodies.get(activeEntity.entityId) : undefined;
    const character = this.bodies.characterDefinition(activeEntity?.characterId ?? state.currentCharacterId);
    const sourceX = activeBody?.body.position.x ?? this.previewX;
    const sourceY = activeBody?.body.position.y ?? this.spawnY();
    const startY = sourceY + character.baseSize * (0.5 - character.centerOfMass.y) + 8;
    this.dropGuide.clear();
    this.dropGuide.lineStyle(2, 0xf4dda0, 0.35);
    for (let y = startY; y < FIELD_HEIGHT - 24; y += 15) {
      this.dropGuide.lineBetween(sourceX, y, sourceX, Math.min(y + 8, FIELD_HEIGHT - 24));
    }
  }

  public startControlledDrop(): void {
    const state = this.getState();
    if (!this.continuousDropEnabled || state.status !== 'playing' || state.activeDropEntityId !== null) return;
    this.bridge.beginDrop(
      this.previewX,
      this.spawnY(),
      this.getStep(),
      this.getGameplayTimeMs(),
      0,
    );
  }

  public maintainControlledFall(): void {
    if (!this.canControl()) return;
    const entityId = this.getState().activeDropEntityId;
    if (!entityId) return;
    const activeBody = this.bodies.get(entityId);
    if (!activeBody) return;
    // Waiting pieces stay outside physics contacts until the player confirms.
    activeBody.image.setIgnoreGravity(true);
    activeBody.body.collisionFilter.mask = 0;
    const attraction = this.getState().cardEffects.attraction;
    this.scene.matter.body.setPosition(activeBody.body, {
      x: activeBody.body.position.x,
      // The attracted stone belongs to this held piece, not the pile beneath it.
      y: this.bodies.dropY(activeBody.characterId, entityId,
        attraction?.targetEntityId === entityId ? attraction.sourceEntityId : undefined),
    });
    activeBody.image.setAwake();
    this.scene.matter.body.setVelocity(activeBody.body, { x: 0, y: 0 });
    this.scene.matter.body.setAngularVelocity(activeBody.body, 0);
  }

  public previewSnapshot(): DropPreviewSnapshot {
    const state = this.getState();
    const activeEntity = state.entities.find((entity) => entity.entityId === state.activeDropEntityId);
    const activeImage = activeEntity ? this.bodies.get(activeEntity.entityId)?.image : undefined;
    const image = activeImage ?? this.preview;
    return {
      characterId: activeEntity?.characterId ?? state.currentCharacterId,
      x: image.x,
      y: image.y,
      originX: image.originX,
      originY: image.originY,
      displayWidth: image.displayWidth,
      displayHeight: image.displayHeight,
      visible: image.visible,
      canControl: this.canControl(),
    };
  }

  public destroy(): void {
    this.reset(false);
    this.preview.destroy();
    this.dropGuide.destroy();
  }

}
